<template>
  <div>
    <ValidationProvider
      ref="mainNightCard"
      v-slot="{ errors, valid }"
      name="mainNightCard"
      :rules="validationRules"
      :allowFalse="false"
      mode="aggressive"
    >
      <VTextField
        :value="card.value"
        clear-icon="mdi-close-circle"
        clearable
        :class="(!!errors.length || card.isBackendValid === false) && !!card.value ? 'pb-6' : ''"
        data-test="another-passenger-main-night-card"
        :label="$t('nightRider.mainPassengerNightCardLabel')"
        :error="!!errors.length || card.isBackendValid === false"
        :error-messages="getErrorMessage(errors[0])"
        @input="onInput($event, valid)"
        @blur="onBlur(valid)"
      />
    </ValidationProvider>
    <SInfoblock
      v-if="showOutOfCommuneDisclaimer"
      color="blue-color"
      class="mb-3"
      :no-margin="true"
      data-test="checkoutNightRiderAnotherPassengerOutOfCommuneDisclaimer"
      :message="$t('nightRider.disclaimerAnotherPassenger')"
    />
    <SInfoblock
      v-if="disclaimerMessage"
      color="blue-color"
      class="mb-3"
      :no-margin="true"
      data-test="checkoutNightRiderValidationErrorDisclaimer"
      :message="disclaimerMessage"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'AnotherPassengerMainNightCard',
};
</script>

<script setup lang="ts">
import { computed, nextTick } from 'vue';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock.vue';
import {
  isNightCardFieldInvalid,
  resolveDisclaimerMessage,
  resolveFieldErrorMessage,
} from '@/utils/nightCardCheckoutLogic';
import { useI18n, useVm, useValidationError } from '@/composables';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  duplicateCards: {
    type: Array,
    default: () => [],
  },
  validationResult: {
    type: [Object, String],
    default: null,
  },
  showOutOfCommuneDisclaimer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'blur']);
const { t, i18n } = useI18n();
const vm = useVm();
const { validationErrorResolve } = useValidationError();

const validationRules = computed(() => {
  if (!(props.card as any).value) {
    return {};
  }
  return {
    min: 7,
    max: 10,
    onlyLettersAndNumbers: /^[a-zA-Z0-9]+$/,
    noDuplicateAmongCards: {
      cards: props.duplicateCards,
      cardValue: (props.card as any).value,
    },
  };
});

const disclaimerMessage = computed(() => resolveDisclaimerMessage(
  props.validationResult as any,
  t as any,
  i18n.locale,
));

const backendFieldErrorMessage = computed(() => {
  if (!isNightCardFieldInvalid(props.validationResult as any)) {
    return '';
  }
  return resolveFieldErrorMessage(props.validationResult as any, t as any);
});

function getErrorMessage(frontError: any) {
  if (frontError) {
    return validationErrorResolve(frontError);
  }
  if ((props.card as any).isBackendValid === false) {
    return backendFieldErrorMessage.value;
  }
  return '';
}

function onInput(value: string, valid: boolean) {
  emit('input', value, valid);
}

function onBlur(valid: boolean) {
  emit('blur', valid);
}

function validate() {
  return new Promise((resolve) => {
    nextTick(() => {
      resolve((vm.$refs as any)?.mainNightCard?.validate?.() ?? true);
    });
  });
}

defineExpose({
  validate,
});
</script>
